'use strict';
import {extendPrototypeResult as localForage} from 'localforage-indexes';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import {IMAGENIE_LOCAL_FORAGE_CONFIG, ImagenieUtil} from '../../_libs/imagenie';
import {getUserId} from '../../auth/reducers';
import {isImageFile} from '../../core-utilities/aws-api/AwsS3GatewayService';
import {AwsS3RequestService} from '../../core-utilities/aws-api/AwsS3RequestService';
import {FILE_COLLECTION} from './config';


export const CLASS_NAME = 'FileDomain';
export const COLLECTION = FILE_COLLECTION;

const localForageConfig = {
  ...IMAGENIE_LOCAL_FORAGE_CONFIG,
  cacheSize: 500,
  lruIndex: `${IMAGENIE_LOCAL_FORAGE_CONFIG.name}-idx`,
};

export function FileDomainProvider() {
  return {
    $get: [
      '$window',
      '$timeout',
      '$interval',
      '$cacheFactory',
      'FirebaseUtil',
      AwsS3RequestService.SERVICE_NAME,
      'AbstractDomainClass',
      'AwsS3GatewayService',
      '$ngRedux',
      'AuthService',
      'FileUtilities',
      'FileUrlCache',

      /**
       * @param $window
       * @param $timeout
       * @param $interval
       * @param $cacheFactory
       * @param FirebaseUtil
       * @param {AwsS3RequestService} awsS3RequestService
       * @param AbstractDomainClass
       * @param AwsS3GatewayService
       * @param $ngRedux
       * @param AuthService
       * @param FileUtilities
       * @param FileUrlCache
       * @return {FileDomain}
       */
      function (
        $window,
        $timeout,
        $interval,
        $cacheFactory,
        FirebaseUtil,
        awsS3RequestService,
        AbstractDomainClass,
        AwsS3GatewayService,
        $ngRedux,
        AuthService,
        FileUtilities,
        FileUrlCache,
      ) {
        const REQUEST_FULL_IMAGE = 'REQUEST_FULL_IMAGE';

        function FileDomain(data) {
          AbstractDomainClass.call(this, FileDomain, data);
        }

        AbstractDomainClass.__inheritFrom(FileDomain, CLASS_NAME, FILE_COLLECTION);

        FileDomain.$constraints.addConstraints({
          attachable: {type: 'DomainLink', nullable: true},
          name: {type: 'string'},
          path: {type: 'string'},
          bucket: {type: 'string'},
          firebasePath: {type: 'string', nullable: true},
          s3Status: {type: 'string', inList: ['pending', 'available']},
          md: {type: 'JSON', nullable: true},
          mimeType: {type: 'string'},
          tb: {type: 'JSON', nullable: true},
          pv: {type: 'JSON', nullable: true},
          uploadedBy: {type: 'DomainLink', domainClass: 'UserDomain'},
          uploadedOn: {type: 'Date'}
        });

        // more methods go here;
        // TODO: add methods to upload on create

        const URL_CACHE = "FileDomain-URL_CACHE";
        const THUMBNAIL_CACHE = "FileDomain-THUMBNAIL_CACHE";

        const urlCache = $cacheFactory.get(URL_CACHE) || $cacheFactory(URL_CACHE);
        //var thumbnailCache = $cacheFactory.get(THUMBNAIL_CACHE) || $cacheFactory(THUMBNAIL_CACHE);

        let imagenieLocalForageInstance = {};

        try {
          imagenieLocalForageInstance = localForage.instance(localForageConfig.name);
        } catch (error) {
          if ($window.ionic && $window.ionic.Platform.isIOS()) {
            //Force WebSQL on IOS as IndexedDB is not stable on IOS
            const iosConfig = Object.assign({}, localForageConfig, {driver: 'webSQLStorage'});
            imagenieLocalForageInstance = localForage.createInstance(iosConfig);
          } else {
            imagenieLocalForageInstance = localForage.createInstance(localForageConfig);
          }
        }

        FileDomain.getFileId = function getFileId(fileDomain) {
          if (isString(fileDomain)) {
            return fileDomain;
          } else if (fileDomain.fileId) {
            return fileDomain.fileId;
          } else if (fileDomain.$id) {
            return fileDomain.$id;
          } else {
            throw new Error('FileDomain.getFileId passed an invalid object!')
          }
        };

        FileDomain.fileIsImage = fileIsImage;

        FileDomain.uploadFile = function (file, data) {
          //console.log('uploading', data, file);
          return AwsS3GatewayService.addFile({
            user: getUserId($ngRedux.getState()),
            bucket: data.bucket || null,
            name: data.name ? (data.name + '.' + FileUtilities.getExtension(file.name)) : null,
            filename: data.filename ? (data.filename + '.' + FileUtilities.getExtension(file.name)) : null,
            path: data.path,
            firebasePath: data.firebasePath || null,
            file: file,
            fileUri: data.fileUri || null, // only required on Cordova platform
            usage: data.usage,
            metadata: data.metadata,
            attachable: data.attachable || null
          });
        };

        FileDomain.prototype.isImage = function () {

          // Hack to short-circuit the check if we already have a preview data:url
          const cachedUrl = FileUrlCache.get(this.$id);
          if (cachedUrl) return true;
          // End Hack

          return FileDomain.fileIsImage(this);
        };

        FileDomain.prototype.getUrl = function () {
          return FileDomain.getUrl(this.$id);
        };

        // We may want to evict in this case too. Not sure yet. Usually this is only supposed to be called for previews of smaller files or downloads
        FileDomain.getUrl = function (id) {
          const fileUrl = FileUrlCache.get(id);

          if (fileUrl && fileUrl.url) {
            return Promise.resolve(fileUrl.url);
          } else {
            return awsS3RequestService.getFileUrl(id);
          }
        };

        // delay up to 2500 ms to wait for thumbs to be available after creation, before creating requests
        FileDomain.prototype.getThumbnailUrl = function (options) {
          const self = this;
          const cachedUrl = FileUrlCache.get(self.$id);
          const existingFile = _getExistingPreview.call(this, options);

          const requestName = existingFile ? existingFile.name : null;
          const cacheId = requestName || `${options.targetWidth}x${options.targetHeight}/${self.$id}`;

          if (cachedUrl && cachedUrl.url) {
            const promise = Promise.resolve(cachedUrl.url);

            // little point in requesting the image if we still have the locally cached copy
            // requesting the url version then evicting might be better for memory usage though
            if (existingFile !== REQUEST_FULL_IMAGE) {
              promise.then(() =>
                imagenieLocalForageInstance.getItem(cacheId)
                  .then(__onGetItemSuccess)
                  .then(__evictCache, __onGetItemFailed.bind(this))
              );
            }

            return promise;
          } else if (existingFile === REQUEST_FULL_IMAGE) {
            return this.getUrl();
          } else {
            return imagenieLocalForageInstance.getItem(cacheId)
              .then(__onGetItemSuccess, __onGetItemFailed);
          }

          function __onGetItemSuccess(localImageSuccessData) {
            // the length check guarantees that even if a key is returned,
            // that the content contains part of the image data after the "data:<mime-type>;base64," prefix
            // otherwise we likely stored a corrupted image and need to re-fetch it.
            if (localImageSuccessData && localImageSuccessData.length > 30) {
              //console.log('retrieved image from cache');
              return localImageSuccessData;
            } else {
              //console.log('failed to retrieve image from cache. received');
              return __onGetItemFailed();
            }
          }

          function __onGetItemFailed() {
            if (existingFile) {
              return _requestPreview.call(self, options.type, existingFile.id, cacheId);
            } else {
              return _waitForPreviewIsReady.call(self, options, cacheId);
            }
          }

          function __evictCache(result) {
            FileUrlCache.remove(self.$id);
            return result
          }
        };

        FileDomain.prototype.toString = function () {
          return this.name;
        };

        function _requestPreview(type, previewId, cacheId) {
          return awsS3RequestService.getFilePreviewUrl(this.$id, type, previewId)
            .then(__createOnGetThumbnailSuccess(cacheId, this));
        }

        function _getExistingPreview(options) {
          const imageSize = this.md && Math.max(this.md.ImageHeight, this.md.ImageWidth);
          const imageSizeMin = this.md && Math.min(this.md.ImageHeight, this.md.ImageWidth);
          const requestedSize = Math.max(options.targetHeight, options.targetWidth);
          const requestedSizeMin = Math.min(options.targetHeight, options.targetWidth);

          if ((imageSize && imageSize < requestedSize) || (imageSizeMin && imageSizeMin <= requestedSizeMin)) {
            // do not cache full size images locally, especially not until we have cache cleanup mechanisms in place
            return REQUEST_FULL_IMAGE;
          } else {
            const existingFiles = (options.type === 'crop' ? this.tb : this.pv) || {};
            let existingFile;
            let currentSize;
            let smallestGreaterThan;
            let smallestGreaterThanSize;

            for (let it in existingFiles) {
              if (existingFiles.hasOwnProperty(it)) {
                currentSize = Math.max(existingFiles[it].h, existingFiles[it].w);
                if (currentSize === requestedSize) {
                  existingFile = existingFiles[it];
                  existingFile.id = it;
                } else if (currentSize > requestedSize && (!smallestGreaterThanSize || smallestGreaterThanSize > currentSize)) {
                  smallestGreaterThanSize = currentSize;
                  smallestGreaterThan = existingFiles[it];
                }
              }
            }

            if (!existingFile && smallestGreaterThan) existingFile = smallestGreaterThan;

            if (!existingFile && options.type !== 'crop') {
              return REQUEST_FULL_IMAGE;
            }

            return existingFile;
          }
        }

        // watch firebase until the thumbnail/preview becomes available
        function _waitForPreviewIsReady(options, cacheId) {
          const deferred = Promise.defer();
          const self = this;
          const type = options.type === 'crop' ? 'tb' : 'pv';

          const cancelInterval = $interval(() => {
            if (isObject(self[type])) {
              const preview = _getExistingPreview.call(self, options);
              if (preview === REQUEST_FULL_IMAGE) {
                self.getUrl().then(deferred.resolve.bind(deferred), deferred.reject.bind(deferred));
              } else {
                _requestPreview.call(self, options.type, preview.id, cacheId)
                  .then(deferred.resolve.bind(deferred), deferred.reject.bind(deferred));
              }
              $interval.cancel(cancelInterval);
            }
          }, 500, 0, false);

          return deferred.promise;
        }

        function __createOnGetThumbnailSuccess(cacheId, context) {
          return function onGetThumbnailSuccess(url) {
            if (url) {
              ImagenieUtil.getImageBase64String(url)
                .then(__createOnBase64ImageSuccess(cacheId), __onBase64ImageError);
              return url;
            } else {
              console.warn('Failed to received file', url, context);
              return null;
            }
          }
        }

        function __createOnBase64ImageSuccess(cacheId) {
          return function onBase64ImageSuccess(imageBase64String) {
            //console.log('Successfully converted to base64. storing with local forage ' + cacheId);
            try {
              imagenieLocalForageInstance.setItem(cacheId, imageBase64String).then(function () {
                //console.log('Successfully stored with local forage');
              }, function (e) {
                console.warn('failed to store with local forage');
                console.error(e);
              });
            } catch (e) {
              console.warn('failed to store with local forage');
              console.error(e);
            }
          }
        }

        function __onBase64ImageError(e) {
          console.warn('failed to convert to base64 with error');
          console.error(e);
        }

        return FileDomain;
      }]
  };
}

export function fileIsImage(file) {
  if (isImageFile(file)) return true;
  let type;
  if (file) {
    if (isString(file.type)) {
      type = file.type
    } else if (isString(file.mimeType)) {
      type = file.mimeType;
    }
    return type && type.indexOf('image') > -1;
  }
  return false;
}



// WEBPACK FOOTER //
// ./src/js/files/domain/FileDomain.js